import { reactive } from 'vue';
import { AllowedQueryParameterKeys } from "./AllowedQueryParameterKeys";

export class QueryParametersState {
	private readonly valuesByKey = reactive(new Map<AllowedQueryParameterKeys, string>());

	public getFor(key: AllowedQueryParameterKeys): string | null {
		return this.valuesByKey.get(key) ?? null;
	}

	public identifySelectedParameters(): readonly AllowedQueryParameterKeys[] {
		return Array.from(this.valuesByKey.keys()).filter((key) => this.getFor(key) !== null);
	}

	public change(key: AllowedQueryParameterKeys, value: string): QueryParametersState {
		const partial = this.copy();

		partial.valuesByKey.set(key, value);

		return partial;
	}

	public isEqual(other: QueryParametersState): boolean {
		const selected = this.identifySelectedParameters();
		const otherSelected = this.identifySelectedParametersFor(other);

		if (selected.length !== otherSelected.length) {
			return false;
		}

		return selected.every((key) => this.isEqualOn(key, other));
	}

	public removeInvalidEntries(state: QueryParametersState): QueryParametersState {
		const result = state.copy();
		for (const key of state.valuesByKey.keys()) {
			if (!this.isValidKey(key)) {
				result.valuesByKey.delete(key);
			}
		}

		return result;
	}

	private isEqualOn(key: AllowedQueryParameterKeys, other: QueryParametersState): boolean {
		const values = this.getFor(key);
		const valuesOther = other.getFor(key);

		return values === valuesOther;
	}

	private isValidKey(key: string): boolean {
		return Object.values(AllowedQueryParameterKeys).includes(key as AllowedQueryParameterKeys);
	}

	private copy(): QueryParametersState {
		const result = new QueryParametersState();
		for (const [key, values] of this.valuesByKey) {
			result.valuesByKey.set(key, values);
		}

		return result;
	}

	private identifySelectedParametersFor(state: QueryParametersState): readonly AllowedQueryParameterKeys[] {
		return Array.from(state.valuesByKey.keys()).filter((key) => state.getFor(key) !== null);
	}
}
